import React, { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../supabaseClient';
import { useCompany } from '../CompanyContext';
import { useImpersonation } from '../ImpersonationContext';
import { hasPlanAccess, PLAN_FEATURES } from '../lib/pricing';
import UpgradeWall from '../components/UpgradeWall';
import { HAZARD_TO_PERMIT_TYPE } from '../constants/hazardPermitMap';
import { logAudit } from '../auditLog';
import SignaturePad, { type SignaturePadHandle } from '../components/permits/SignaturePad';
import PhotoCapture, { type OrigenFoto } from '../components/permits/PhotoCapture';
import {
  RESULTADO_LABELS, ROL_FIRMA_LABELS, vigenteHastaPorDefecto, siguienteResultado, estadoChecklist,
  faltantesParaActivar, faltantesParaCerrar, porcentajeRespondido,
  type ResultadoPregunta, type EtapaFirma, type RolFirma,
} from '../lib/permisos-trabajo';
import {
  rutaEvidencia, subirEvidencia, urlsFirmadas, borrarEvidencias, evidenciaComoDataUrl,
} from '../lib/permisos-evidencia';
import { generarPermisoPDF, fechaHoraEC, nombreArchivoPermiso } from '../lib/permiso-pdf';
import { esColumnaInexistente, validarTextoPregunta, mensajeErrorPlantilla, plantillasACopiar } from '../lib/permisos-plantillas';
import { useMediaQuery, CONSULTA_MOVIL } from '../lib/useMediaQuery';
import ProcedimientosPanel from '../components/informes-puesto/ProcedimientosPanel';

// ── Types ──────────────────────────────────────────────────────────────────────

type PermitType   = 'altura' | 'caliente' | 'confinado' | 'electrico' | 'quimico' | 'loto' | 'excavacion' | 'maquinaria' | 'vias';
type PermitStatus = 'pendiente' | 'activo' | 'cerrado' | 'vencido' | 'cancelado';

interface WorkPermit {
  id:               string;
  company_id:       string;
  permit_type:      PermitType;
  warehouse_id:     string | null;
  worker_id:        string | null;
  work_description: string;
  location:         string;
  valid_from:       string;
  valid_until:      string;
  authorized_by:    string;
  executed_by:      string;
  watcher_name:     string | null;
  status:           PermitStatus;
  closure_notes:    string | null;
  closed_at:        string | null;
  activated_at:     string | null;
  created_by:       string | null;
  created_at:       string;
  updated_at:       string;
}

interface WorkPermitChecklistItem {
  id:            string;
  permit_type:   PermitType;
  item_text:     string;
  display_order: number;
  referencia?:   string | null;
}

interface WorkPermitResponse {
  permit_id:         string;
  checklist_item_id: string;
  resultado:         ResultadoPregunta | null;
}

interface CustomItem {
  id:            string;
  permit_id:     string;
  item_text:     string;
  display_order: number;
  resultado:     ResultadoPregunta | null;
  observation:   string | null;
}

// Plantilla de pregunta propia (M7). `active` llega con M12; sin M12 es undefined.
interface PlantillaPregunta {
  id:            string;
  permit_type:   PermitType;
  item_text:     string;
  display_order: number;
  active?:       boolean;
  created_at:    string;
}

interface Firma {
  id:         string;
  etapa:      EtapaFirma;
  rol:        RolFirma;
  nombre:     string;
  cedula:     string | null;
  firma_path: string;
  firmado_en: string;
}

interface Foto {
  id:        string;
  foto_path: string;
  origen:    OrigenFoto;
  tomada_en: string;
}

interface WorkerOption   { id: string; full_name: string; cedula: string | null; position_id: string | null; }

interface HazardCatalogItem { id: string; category: string; hazard_type: string; }

// ── Constants ──────────────────────────────────────────────────────────────────

const PERMIT_TYPE_LABELS: Record<PermitType, string> = {
  altura:     'Trabajo en altura',
  caliente:   'Trabajo en caliente',
  confinado:  'Espacio confinado',
  electrico:  'Trabajo eléctrico',
  quimico:    'Químico / Sustancias Peligrosas',
  loto:       'Bloqueo y Etiquetado (LOTO)',
  excavacion: 'Excavación / Zanjas',
  maquinaria: 'Maquinaria Pesada',
  vias:       'Trabajo en Vías Públicas',
};

const PERMIT_TYPE_COLORS: Record<PermitType, { bg: string; color: string; border: string }> = {
  altura:     { bg: 'rgba(59,130,246,0.12)',  color: '#60a5fa', border: 'rgba(59,130,246,0.3)'  },
  caliente:   { bg: 'rgba(239,68,68,0.12)',   color: '#ef4444', border: 'rgba(239,68,68,0.3)'   },
  confinado:  { bg: 'rgba(167,139,250,0.12)', color: '#a78bfa', border: 'rgba(167,139,250,0.3)' },
  electrico:  { bg: 'rgba(234,179,8,0.12)',   color: '#eab308', border: 'rgba(234,179,8,0.3)'   },
  quimico:    { bg: 'rgba(34,197,94,0.12)',   color: '#22c55e', border: 'rgba(34,197,94,0.3)'   },
  loto:       { bg: 'rgba(249,115,22,0.12)',  color: '#f97316', border: 'rgba(249,115,22,0.3)'  },
  excavacion: { bg: 'rgba(180,83,9,0.12)',    color: '#b45309', border: 'rgba(180,83,9,0.3)'    },
  maquinaria: { bg: 'rgba(100,116,139,0.12)', color: '#64748b', border: 'rgba(100,116,139,0.3)' },
  vias:       { bg: 'rgba(236,72,153,0.12)',  color: '#ec4899', border: 'rgba(236,72,153,0.3)'  },
};

const STATUS_LABELS: Record<PermitStatus, string> = {
  pendiente: 'Pendiente de firmas',
  activo:    'Activo',
  cerrado:   'Cerrado',
  vencido:   'Vencido',
  cancelado: 'Cancelado',
};

const RESULTADO_COLORS: Record<ResultadoPregunta, { bg: string; color: string }> = {
  cumple:    { bg: 'rgba(47,191,113,0.2)',  color: '#2FBF71' },
  no_cumple: { bg: 'rgba(239,68,68,0.18)',  color: '#ef4444' },
  no_aplica: { bg: 'rgba(148,163,184,0.18)', color: '#cbd5e1' },
};

function makeEmptyForm() {
  const now = new Date();
  return {
    permit_type:      'altura' as PermitType,
    work_description: '',
    location:         '',
    worker_id:        '',
    valid_from:       toLocalDateTimeInput(now),
    valid_until:      toLocalDateTimeInput(vigenteHastaPorDefecto(now)),
    authorized_by:    '',
    executed_by:      '',
    watcher_name:     '',
  };
}

function toLocalDateTimeInput(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// ── Component ──────────────────────────────────────────────────────────────────

export default function WorkPermitsPage() {
  const { company, profile } = useCompany();
  const { impersonated }     = useImpersonation();
  const displayCompany       = impersonated ?? company;
  const companyId            = displayCompany?.id ?? '';

  const canEdit   = profile?.role === 'admin' || profile?.role === 'manager' || !!impersonated;
  const canDelete = profile?.role === 'admin' || !!impersonated;

  // ── State ─────────────────────────────────────────────────────────────────

  const [view,         setView]         = useState<'list' | 'checklist'>('list');
  // Lote D (GT 37): procedimientos de cada tipo de trabajo especial y su socialización.
  const [verProcedimientos, setVerProcedimientos] = useState(false);
  const [activePermit, setActivePermit] = useState<WorkPermit | null>(null);

  const [permits,      setPermits]      = useState<WorkPermit[]>([]);
  const [workers,      setWorkers]      = useState<WorkerOption[]>([]);
  const [responses,    setResponses]    = useState<WorkPermitResponse[]>([]);
  const [catalogIds,   setCatalogIds]   = useState<Record<string, string[]>>({});
  const [customAll,    setCustomAll]    = useState<{ permit_id: string; resultado: ResultadoPregunta | null }[]>([]);
  const [loading,      setLoading]      = useState(true);

  const [filterType,   setFilterType]   = useState<PermitType | ''>('');
  const [filterStatus, setFilterStatus] = useState<PermitStatus | ''>('');
  const [filterSearch, setFilterSearch] = useState('');

  const [showCreate,      setShowCreate]      = useState(false);
  const [form,            setForm]            = useState(makeEmptyForm);
  const [hastaEditado,    setHastaEditado]    = useState(false);
  const [creating,        setCreating]        = useState(false);
  const [trainingWarning, setTrainingWarning] = useState<string | null>(null);

  const [hazardCatalog,          setHazardCatalog]          = useState<HazardCatalogItem[]>([]);
  const [suggestedPermitTypes,   setSuggestedPermitTypes]   = useState<PermitType[]>([]);

  const [checklistItems,   setChecklistItems]   = useState<WorkPermitChecklistItem[]>([]);
  const [checklistLoading, setChecklistLoading] = useState(false);
  const [responseState,    setResponseState]    = useState<Record<string, { resultado: ResultadoPregunta | null; observation: string }>>({});
  const [customItems,      setCustomItems]      = useState<CustomItem[]>([]);
  const [savingItem,       setSavingItem]       = useState<Record<string, boolean>>({});

  const [newQuestion,      setNewQuestion]      = useState('');
  const [saveAsTemplate,   setSaveAsTemplate]   = useState(false);
  const [showAddQuestion,  setShowAddQuestion]  = useState(false);
  const [addingQuestion,   setAddingQuestion]   = useState(false);

  const [firmas,       setFirmas]       = useState<Firma[]>([]);
  const [fotos,        setFotos]        = useState<Foto[]>([]);
  const [evidenceUrls, setEvidenceUrls] = useState<Record<string, string>>({});
  const [sigNombre,    setSigNombre]    = useState('');
  const [sigCedula,    setSigCedula]    = useState('');
  const [sigRol,       setSigRol]       = useState<RolFirma>('autoriza');
  const [sigVacia,     setSigVacia]     = useState(true);
  const [savingSig,    setSavingSig]    = useState(false);
  const [savingFoto,   setSavingFoto]   = useState(false);
  const [activating,   setActivating]   = useState(false);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const sigPadRef   = useRef<SignaturePadHandle>(null);
  const closePadRef = useRef<SignaturePadHandle>(null);

  const [closing,          setClosing]          = useState(false);
  const [showCloseModal,   setShowCloseModal]   = useState(false);
  const [closureNotes,     setClosureNotes]     = useState('');
  const [closeNombre,      setCloseNombre]      = useState('');
  const [closeCedula,      setCloseCedula]      = useState('');
  const [closeSigVacia,    setCloseSigVacia]    = useState(true);
  const [showCancelModal,  setShowCancelModal]  = useState(false);
  const [cancelReason,     setCancelReason]     = useState('');
  const [cancelling,       setCancelling]       = useState(false);

  const [deleteTarget, setDeleteTarget] = useState<WorkPermit | null>(null);
  const [deleteStep,   setDeleteStep]   = useState(1);
  const [deleting,     setDeleting]     = useState(false);

  // Preguntas propias guardadas (§17 ítem 88). m12 = null mientras no se sabe
  // si la columna `active` existe; false = sin M12 (no se puede desactivar).
  const [showPlantillas,   setShowPlantillas]   = useState(false);
  const [plantillas,       setPlantillas]       = useState<PlantillaPregunta[]>([]);
  const [plantillasCargando, setPlantillasCargando] = useState(false);
  const [m12,              setM12]              = useState<boolean | null>(null);
  const [filtroPlantilla,  setFiltroPlantilla]  = useState<PermitType | ''>('');
  const [editandoId,       setEditandoId]       = useState<string | null>(null);
  const [textoEditado,     setTextoEditado]     = useState('');
  const [borrarPlantilla,  setBorrarPlantilla]  = useState<{ id: string; paso: 1 | 2 } | null>(null);
  const [plantillaOcupada, setPlantillaOcupada] = useState<string | null>(null);
  const [plantillaMsg,     setPlantillaMsg]     = useState<{ tipo: 'error' | 'ok'; texto: string } | null>(null);

  const esMovil = useMediaQuery(CONSULTA_MOVIL);

  // ── Load ────────────────────────────────────────────────────────────────────

  const loadData = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    const [{ data: perms }, { data: resps }, { data: wks }, { data: cat }, { data: custom }] = await Promise.all([
      supabase.from('work_permits').select('*').eq('company_id', companyId).order('created_at', { ascending: false }),
      supabase.from('work_permit_responses').select('permit_id, checklist_item_id, resultado').eq('company_id', companyId),
      supabase.from('workers').select('id, full_name, cedula, position_id').eq('company_id', companyId).eq('status', 'activo').order('full_name'),
      supabase.from('work_permit_checklist_items').select('id, permit_type'),
      supabase.from('work_permit_custom_items').select('permit_id, resultado').eq('company_id', companyId),
    ]);
    const ids: Record<string, string[]> = {};
    ((cat ?? []) as { id: string; permit_type: string }[]).forEach(i => { (ids[i.permit_type] ??= []).push(i.id); });
    setPermits((perms ?? []) as WorkPermit[]);
    setWorkers((wks ?? []) as WorkerOption[]);
    setResponses((resps ?? []) as WorkPermitResponse[]);
    setCatalogIds(ids);
    setCustomAll((custom ?? []) as { permit_id: string; resultado: ResultadoPregunta | null }[]);
    setLoading(false);
  }, [companyId]);

  useEffect(() => { loadData(); }, [loadData]);

  // ── Cross-validation: training vigente para el tipo de permiso ───────────────

  useEffect(() => {
    if (!showCreate || !form.worker_id || !companyId) {
      setTrainingWarning(null);
      return;
    }
    const permitTypes = ['altura', 'caliente', 'confinado', 'electrico', 'quimico', 'loto', 'excavacion', 'maquinaria', 'vias'];
    if (!permitTypes.includes(form.permit_type)) {
      setTrainingWarning(null);
      return;
    }

    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from('sso_training_attendance')
        .select('sso_trainings(training_type, scheduled_date, validity_months)')
        .eq('company_id', companyId)
        .eq('worker_id', form.worker_id)
        .eq('attended', true);

      if (cancelled) return;

      const now = new Date();
      const hasValid = (data ?? []).some((att: any) => {
        const tr = att.sso_trainings;
        if (!tr || tr.training_type !== form.permit_type) return false;
        const expiry = new Date(tr.scheduled_date + 'T00:00:00');
        expiry.setMonth(expiry.getMonth() + (tr.validity_months ?? 12));
        return expiry > now;
      });

      if (!hasValid) {
        setTrainingWarning(PERMIT_TYPE_LABELS[form.permit_type as PermitType] ?? form.permit_type);
      } else {
        setTrainingWarning(null);
      }
    })();

    return () => { cancelled = true; };
  }, [form.worker_id, form.permit_type, showCreate, companyId]);

  // ── Catálogo de peligros IPER (para sugerir permit_type según el puesto) ─────

  useEffect(() => {
    supabase.from('iper_hazard_catalog').select('id, category, hazard_type')
      .then(({ data }) => setHazardCatalog((data ?? []) as HazardCatalogItem[]));
  }, []);

  // ── Sugerencia de tipo de permiso según riesgos típicos del puesto ──────────

  useEffect(() => {
    if (!showCreate || !form.worker_id) { setSuggestedPermitTypes([]); return; }
    const worker = workers.find(w => w.id === form.worker_id);
    const positionId = worker?.position_id;
    if (!positionId) { setSuggestedPermitTypes([]); return; }

    let cancelled = false;
    supabase
      .from('job_position_typical_hazards')
      .select('hazard_catalog_id')
      .eq('job_position_id', positionId)
      .then(({ data }) => {
        if (cancelled) return;
        const rows = (data ?? []) as { hazard_catalog_id: string }[];
        const types = new Set<PermitType>();
        rows.forEach(r => {
          const item = hazardCatalog.find(c => c.id === r.hazard_catalog_id);
          if (!item) return;
          if (item.category === 'quimico') { types.add('quimico'); return; }
          const mapped = HAZARD_TO_PERMIT_TYPE[item.hazard_type];
          if (mapped) types.add(mapped);
        });
        setSuggestedPermitTypes(Array.from(types));
      });
    return () => { cancelled = true; };
  }, [form.worker_id, showCreate, workers, hazardCatalog]);

  // ── Helpers ──────────────────────────────────────────────────────────────────

  function calcProgress(permit: WorkPermit): number {
    const ids = catalogIds[permit.permit_type] ?? [];
    const porItem = new Map(responses.filter(r => r.permit_id === permit.id).map(r => [r.checklist_item_id, r.resultado]));
    return porcentajeRespondido(estadoChecklist(
      ids.map(id => ({ resultado: porItem.get(id) ?? null })),
      customAll.filter(c => c.permit_id === permit.id),
    ));
  }

  function isExpired(permit: WorkPermit): boolean {
    return new Date(permit.valid_until).getTime() < Date.now();
  }

  // ── Metrics ──────────────────────────────────────────────────────────────────

  const totalPermits   = permits.length;
  const pendingCount    = permits.filter(p => p.status === 'pendiente').length;
  const activeNow       = permits.filter(p => p.status === 'activo' && !isExpired(p)).length;
  const expiredOpen     = permits.filter(p => p.status === 'activo' && isExpired(p)).length;
  const closedCount     = permits.filter(p => p.status === 'cerrado').length;

  // ── Filters ──────────────────────────────────────────────────────────────────

  const filteredPermits = permits.filter(p => {
    if (filterType && p.permit_type !== filterType) return false;
    if (filterStatus && p.status !== filterStatus) return false;
    if (filterSearch) {
      const q = filterSearch.toLowerCase();
      if (!p.work_description.toLowerCase().includes(q) && !p.location.toLowerCase().includes(q)) return false;
    }
    return true;
  });

  // ── Checklist open ───────────────────────────────────────────────────────────

  const loadEvidence = useCallback(async (permitId: string) => {
    const [{ data: fs }, { data: ph }] = await Promise.all([
      supabase.from('work_permit_signatures').select('id, etapa, rol, nombre, cedula, firma_path, firmado_en').eq('permit_id', permitId).order('firmado_en'),
      supabase.from('work_permit_photos').select('id, foto_path, origen, tomada_en').eq('permit_id', permitId).order('tomada_en'),
    ]);
    const firmasList = (fs ?? []) as Firma[];
    const fotosList  = (ph ?? []) as Foto[];
    setFirmas(firmasList);
    setFotos(fotosList);
    setEvidenceUrls(await urlsFirmadas([...firmasList.map(f => f.firma_path), ...fotosList.map(f => f.foto_path)]));
  }, []);

  async function openChecklist(permit: WorkPermit) {
    setActivePermit(permit);
    setView('checklist');
    setChecklistLoading(true);
    setShowAddQuestion(false);
    setNewQuestion('');
    setSaveAsTemplate(false);
    setSigNombre(permit.authorized_by);
    setSigCedula('');
    setSigRol('autoriza');
    const [{ data: items }, { data: resps }, { data: custom }] = await Promise.all([
      supabase.from('work_permit_checklist_items').select('*').eq('permit_type', permit.permit_type).order('display_order'),
      supabase.from('work_permit_responses').select('*').eq('permit_id', permit.id),
      supabase.from('work_permit_custom_items').select('id, permit_id, item_text, display_order, resultado, observation').eq('permit_id', permit.id).order('display_order'),
    ]);
    const itemList = (items ?? []) as WorkPermitChecklistItem[];
    setChecklistItems(itemList);
    setCustomItems((custom ?? []) as CustomItem[]);

    const map: Record<string, { resultado: ResultadoPregunta | null; observation: string }> = {};
    (resps ?? []).forEach((r: any) => {
      map[r.checklist_item_id] = { resultado: r.resultado ?? null, observation: r.observation ?? '' };
    });
    itemList.forEach(item => {
      if (!map[item.id]) map[item.id] = { resultado: null, observation: '' };
    });
    setResponseState(map);
    await loadEvidence(permit.id);
    setChecklistLoading(false);
  }

  // ── Create ───────────────────────────────────────────────────────────────────

  async function handleCreate() {
    if (!form.work_description.trim()) { alert('La descripción del trabajo es requerida.'); return; }
    if (!form.location.trim()) { alert('La ubicación es requerida.'); return; }
    if (!form.valid_until) { alert('La fecha de vigencia hasta es requerida.'); return; }
    if (new Date(form.valid_until) <= new Date(form.valid_from)) { alert('"Vigente hasta" debe ser posterior a "Vigente desde".'); return; }
    if (!form.authorized_by.trim()) { alert('El campo "Autorizado por" es requerido.'); return; }
    if (!form.executed_by.trim()) { alert('El campo "Ejecutado por" es requerido.'); return; }

    setCreating(true);
    // Nace pendiente: se activa con el checklist y las firmas (bloque 5).
    // warehouse_id ya no se pide en la pantalla (A1); la columna sigue en la base.
    const { data, error } = await supabase
      .from('work_permits')
      .insert({
        company_id:       companyId,
        permit_type:      form.permit_type,
        worker_id:        form.worker_id || null,
        work_description: form.work_description.trim(),
        location:         form.location.trim(),
        valid_from:        new Date(form.valid_from).toISOString(),
        valid_until:       new Date(form.valid_until).toISOString(),
        authorized_by:     form.authorized_by.trim(),
        executed_by:       form.executed_by.trim(),
        watcher_name:      form.watcher_name.trim() || null,
        status:            'pendiente',
        created_by:        profile?.id ?? null,
      })
      .select('*')
      .single();
    if (error) { alert('Error al crear permiso: ' + error.message); setCreating(false); return; }
    const permit = data as WorkPermit;

    // Preguntas guardadas por la empresa para este tipo (A3). Solo las activas
    // (M12); sin la columna `active` se copian todas, como antes.
    const consultaPlantillas = (columnas: string) => supabase
      .from('work_permit_custom_templates')
      .select(columnas)
      .eq('company_id', companyId)
      .eq('permit_type', permit.permit_type)
      .order('display_order');
    const conActive = await consultaPlantillas('id, item_text, display_order, active');
    const tplData = esColumnaInexistente(conActive.error)
      ? (await consultaPlantillas('id, item_text, display_order')).data
      : conActive.data;
    const templates = plantillasACopiar((tplData ?? []) as unknown as { id: string; item_text: string; active?: boolean }[]);
    if (templates.length > 0) {
      const { error: tplErr } = await supabase.from('work_permit_custom_items').insert(
        templates.map((t, i) => ({
          company_id: companyId, permit_id: permit.id, template_id: t.id,
          item_text: t.item_text, display_order: i + 1, created_by: profile?.id ?? null,
        })),
      );
      if (tplErr) alert('El permiso se creó, pero no se pudieron copiar las preguntas guardadas: ' + tplErr.message);
    }

    logAudit({
      company_id: companyId, action: 'created', entity_type: 'work_permit', entity_id: permit.id,
      entity_name: `${PERMIT_TYPE_LABELS[permit.permit_type]} — ${permit.work_description}`,
      details: { permit_type: permit.permit_type, status: 'pendiente', preguntas_propias: templates.length },
    });

    setCreating(false);
    setShowCreate(false);
    setForm(makeEmptyForm());
    await loadData();
    openChecklist(permit);
  }

  // ── Checklist responses ──────────────────────────────────────────────────────

  async function handleResultado(itemId: string, pulsado: ResultadoPregunta) {
    if (!activePermit || isLockedChecklist) return;
    const prev = responseState[itemId] ?? { resultado: null, observation: '' };
    const next = siguienteResultado(prev.resultado, pulsado);
    setResponseState(s => ({ ...s, [itemId]: { ...prev, resultado: next } }));
    setSavingItem(s => ({ ...s, [itemId]: true }));

    const { error } = await supabase
      .from('work_permit_responses')
      .upsert({
        permit_id:         activePermit.id,
        checklist_item_id: itemId,
        company_id:         companyId,
        resultado:          next,
        verified:           next === 'cumple' || next === 'no_aplica',
        observation:        prev.observation || null,
      }, { onConflict: 'permit_id,checklist_item_id' });

    setSavingItem(s => ({ ...s, [itemId]: false }));
    if (error) {
      alert('Error al guardar la respuesta: ' + error.message);
      setResponseState(s => ({ ...s, [itemId]: prev }));
    } else {
      setResponses(rs => [...rs.filter(r => !(r.permit_id === activePermit.id && r.checklist_item_id === itemId)),
        { permit_id: activePermit.id, checklist_item_id: itemId, resultado: next }]);
    }
  }

  async function handleObservationBlur(itemId: string, observation: string) {
    if (!activePermit || isLockedChecklist) return;
    const current = responseState[itemId];
    if (!current) return;

    setSavingItem(s => ({ ...s, [itemId]: true }));
    const { error } = await supabase
      .from('work_permit_responses')
      .upsert({
        permit_id:         activePermit.id,
        checklist_item_id: itemId,
        company_id:         companyId,
        resultado:          current.resultado,
        verified:           current.resultado === 'cumple' || current.resultado === 'no_aplica',
        observation:        observation.trim() || null,
      }, { onConflict: 'permit_id,checklist_item_id' });
    setSavingItem(s => ({ ...s, [itemId]: false }));
    if (error) alert('Error al guardar observación: ' + error.message);
  }

  // ── Preguntas propias (A3) ───────────────────────────────────────────────────

  async function handleCustomResultado(item: CustomItem, pulsado: ResultadoPregunta) {
    if (isLockedChecklist) return;
    const next = siguienteResultado(item.resultado, pulsado);
    setCustomItems(cs => cs.map(c => c.id === item.id ? { ...c, resultado: next } : c));
    setSavingItem(s => ({ ...s, [item.id]: true }));
    const { error } = await supabase
      .from('work_permit_custom_items')
      .update({ resultado: next, updated_at: new Date().toISOString() })
      .eq('id', item.id);
    setSavingItem(s => ({ ...s, [item.id]: false }));
    if (error) {
      alert('Error al guardar la respuesta: ' + error.message);
      setCustomItems(cs => cs.map(c => c.id === item.id ? item : c));
    } else {
      loadData();
    }
  }

  async function handleCustomObservationBlur(item: CustomItem, observation: string) {
    if (isLockedChecklist) return;
    const { error } = await supabase
      .from('work_permit_custom_items')
      .update({ observation: observation.trim() || null, updated_at: new Date().toISOString() })
      .eq('id', item.id);
    if (error) alert('Error al guardar observación: ' + error.message);
  }

  async function handleAddQuestion() {
    if (!activePermit) return;
    const texto = newQuestion.trim();
    if (!texto) { alert('Escribe la pregunta.'); return; }
    if (texto.length > 500) { alert('La pregunta no puede pasar de 500 caracteres.'); return; }
    setAddingQuestion(true);

    let templateId: string | null = null;
    if (saveAsTemplate) {
      const { data: tpl, error: tplErr } = await supabase
        .from('work_permit_custom_templates')
        .insert({ company_id: companyId, permit_type: activePermit.permit_type, item_text: texto, created_by: profile?.id ?? null })
        .select('id')
        .single();
      if (tplErr && tplErr.code !== '23505') {
        alert('No se pudo guardar la pregunta para los próximos permisos: ' + tplErr.message);
      }
      templateId = (tpl as { id: string } | null)?.id ?? null;
      if (templateId) {
        logAudit({
          company_id: companyId, action: 'created', entity_type: 'work_permit_template', entity_id: templateId,
          entity_name: `${PERMIT_TYPE_LABELS[activePermit.permit_type]} — ${texto}`,
          details: { evento: 'plantilla_creada', permit_type: activePermit.permit_type, pregunta: texto, desde_permiso: activePermit.id },
        });
      }
    }

    const order = customItems.reduce((m, c) => Math.max(m, c.display_order), 0) + 1;
    const { data, error } = await supabase
      .from('work_permit_custom_items')
      .insert({
        company_id: companyId, permit_id: activePermit.id, template_id: templateId,
        item_text: texto, display_order: order, created_by: profile?.id ?? null,
      })
      .select('id, permit_id, item_text, display_order, resultado, observation')
      .single();
    setAddingQuestion(false);
    if (error) { alert('Error al agregar la pregunta: ' + error.message); return; }

    setCustomItems(cs => [...cs, data as CustomItem]);
    logAudit({
      company_id: companyId, action: 'updated', entity_type: 'work_permit', entity_id: activePermit.id,
      entity_name: `${PERMIT_TYPE_LABELS[activePermit.permit_type]} — ${activePermit.work_description}`,
      details: { evento: 'pregunta_agregada', pregunta: texto, guardada_para_el_tipo: saveAsTemplate },
    });
    setNewQuestion('');
    setSaveAsTemplate(false);
    setShowAddQuestion(false);
    loadData();
  }

  async function handleDeleteQuestion(item: CustomItem) {
    if (isLockedChecklist) return;
    const { error } = await supabase.from('work_permit_custom_items').delete().eq('id', item.id);
    if (error) { alert('Error al quitar la pregunta: ' + error.message); return; }
    setCustomItems(cs => cs.filter(c => c.id !== item.id));
    loadData();
  }

  // ── Preguntas propias guardadas (plantillas, §17 ítem 88) ────────────────────

  const cargarPlantillas = useCallback(async () => {
    if (!companyId) return;
    setPlantillasCargando(true);
    const consulta = (columnas: string) => supabase
      .from('work_permit_custom_templates')
      .select(columnas)
      .eq('company_id', companyId)
      .order('permit_type')
      .order('display_order')
      .order('created_at');
    const conActive = await consulta('id, permit_type, item_text, display_order, active, created_at');
    let filas = conActive.data as unknown as PlantillaPregunta[] | null;
    let error = conActive.error;
    if (esColumnaInexistente(conActive.error)) {
      const sinActive = await consulta('id, permit_type, item_text, display_order, created_at');
      filas = sinActive.data as unknown as PlantillaPregunta[] | null;
      error = sinActive.error;
      setM12(false);
    } else {
      setM12(true);
    }
    if (error) {
      console.error('Error al cargar las preguntas propias:', error.message);
      setPlantillaMsg({ tipo: 'error', texto: 'No se pudieron cargar las preguntas guardadas.' });
    }
    setPlantillas(filas ?? []);
    setPlantillasCargando(false);
  }, [companyId]);

  function abrirPlantillas() {
    setPlantillaMsg(null);
    setEditandoId(null);
    setBorrarPlantilla(null);
    setShowPlantillas(true);
    cargarPlantillas();
  }

  function cerrarPlantillas() {
    if (plantillaOcupada) return;
    setShowPlantillas(false);
    setEditandoId(null);
    setBorrarPlantilla(null);
    setPlantillaMsg(null);
  }

  function nombrePlantilla(p: PlantillaPregunta, texto = p.item_text) {
    return `${PERMIT_TYPE_LABELS[p.permit_type]} — ${texto}`;
  }

  async function guardarTextoPlantilla(p: PlantillaPregunta) {
    const texto = textoEditado.trim();
    const invalido = validarTextoPregunta(texto);
    if (invalido) { setPlantillaMsg({ tipo: 'error', texto: invalido }); return; }
    if (texto === p.item_text) { setEditandoId(null); return; }
    setPlantillaOcupada(p.id);
    const cambios: Record<string, unknown> = { item_text: texto };
    if (m12) cambios.updated_at = new Date().toISOString();
    const { error } = await supabase.from('work_permit_custom_templates').update(cambios).eq('id', p.id);
    setPlantillaOcupada(null);
    if (error) { console.error('Error al editar la pregunta propia:', error.message); setPlantillaMsg({ tipo: 'error', texto: mensajeErrorPlantilla(error) }); return; }
    logAudit({
      company_id: companyId, action: 'updated', entity_type: 'work_permit_template', entity_id: p.id,
      entity_name: nombrePlantilla(p, texto),
      details: { evento: 'plantilla_editada', permit_type: p.permit_type, antes: p.item_text, despues: texto },
    });
    setPlantillas(ps => ps.map(x => x.id === p.id ? { ...x, item_text: texto } : x));
    setEditandoId(null);
    setPlantillaMsg({ tipo: 'ok', texto: 'Pregunta actualizada. Los permisos ya creados conservan el texto anterior.' });
  }

  async function cambiarActivaPlantilla(p: PlantillaPregunta) {
    if (!m12) return;
    const activa = p.active === false;
    setPlantillaOcupada(p.id);
    const { error } = await supabase.from('work_permit_custom_templates')
      .update({ active: activa, updated_at: new Date().toISOString() }).eq('id', p.id);
    setPlantillaOcupada(null);
    if (error) { console.error('Error al cambiar el estado de la pregunta propia:', error.message); setPlantillaMsg({ tipo: 'error', texto: mensajeErrorPlantilla(error) }); return; }
    logAudit({
      company_id: companyId, action: 'updated', entity_type: 'work_permit_template', entity_id: p.id,
      entity_name: nombrePlantilla(p),
      details: { evento: activa ? 'plantilla_activada' : 'plantilla_desactivada', permit_type: p.permit_type, pregunta: p.item_text },
    });
    setPlantillas(ps => ps.map(x => x.id === p.id ? { ...x, active: activa } : x));
    setPlantillaMsg({ tipo: 'ok', texto: activa ? 'La pregunta se volverá a copiar en los permisos nuevos.' : 'La pregunta ya no se copiará en los permisos nuevos. Los permisos ya creados la conservan.' });
  }

  async function borrarPlantillaDefinitivo(p: PlantillaPregunta) {
    if (!canDelete) return;
    setPlantillaOcupada(p.id);
    // .select() devuelve las filas borradas: si la política no deja, llega vacío sin error.
    const { data, error } = await supabase.from('work_permit_custom_templates').delete().eq('id', p.id).select('id');
    setPlantillaOcupada(null);
    setBorrarPlantilla(null);
    if (error || !data || data.length === 0) {
      if (error) console.error('Error al borrar la pregunta propia:', error.message);
      setPlantillaMsg({ tipo: 'error', texto: error ? mensajeErrorPlantilla(error) : 'No se pudo borrar la pregunta (sin permiso o ya no existe).' });
      return;
    }
    logAudit({
      company_id: companyId, action: 'deleted', entity_type: 'work_permit_template', entity_id: p.id,
      entity_name: nombrePlantilla(p),
      details: { evento: 'plantilla_borrada', permit_type: p.permit_type, pregunta: p.item_text },
    });
    setPlantillas(ps => ps.filter(x => x.id !== p.id));
    setPlantillaMsg({ tipo: 'ok', texto: 'Pregunta borrada. Los permisos ya creados la conservan.' });
  }

  // ── Firmas y fotos (A4) ──────────────────────────────────────────────────────

  function auditEvidencia(evento: string, details: Record<string, unknown>) {
    if (!activePermit) return;
    logAudit({
      company_id: companyId, action: 'updated', entity_type: 'work_permit', entity_id: activePermit.id,
      entity_name: `${PERMIT_TYPE_LABELS[activePermit.permit_type]} — ${activePermit.work_description}`,
      details: { evento, ...details },
    });
  }

  // Sube la firma y registra la fila; si la fila falla, borra el archivo.
  async function guardarFirma(pad: SignaturePadHandle | null, etapa: EtapaFirma, rol: RolFirma, nombre: string, cedula: string): Promise<boolean> {
    if (!activePermit) return false;
    const blob = await pad?.toBlob();
    if (!blob) { alert('Falta la firma.'); return false; }
    const path = rutaEvidencia(companyId, activePermit.id, 'firmas');
    const upErr = await subirEvidencia(path, blob);
    if (upErr) { alert('Error al subir la firma: ' + upErr); return false; }
    const { data, error } = await supabase
      .from('work_permit_signatures')
      .insert({
        company_id: companyId, permit_id: activePermit.id, etapa, rol,
        nombre: nombre.trim(), cedula: cedula.trim() || null, firma_path: path, created_by: profile?.id ?? null,
      })
      .select('id, firmado_en')
      .single();
    if (error) {
      await borrarEvidencias([path]);
      alert('Error al guardar la firma: ' + error.message);
      return false;
    }
    auditEvidencia('firma_registrada', { etapa, rol, nombre: nombre.trim(), firma_id: (data as { id: string }).id, firmado_en: (data as { firmado_en: string }).firmado_en });
    return true;
  }

  async function handleAddSignature() {
    if (!sigNombre.trim()) { alert('El nombre de quien firma es requerido.'); return; }
    setSavingSig(true);
    const ok = await guardarFirma(sigPadRef.current, 'verificacion', sigRol, sigNombre, sigCedula);
    setSavingSig(false);
    if (!ok || !activePermit) return;
    sigPadRef.current?.clear();
    // Después de quien autoriza suelen firmar los ejecutores.
    setSigNombre('');
    setSigCedula('');
    setSigRol('ejecutor');
    await loadEvidence(activePermit.id);
  }

  async function handleDeleteSignature(f: Firma) {
    if (!activePermit || isLockedChecklist) return;
    const { error } = await supabase.from('work_permit_signatures').delete().eq('id', f.id);
    if (error) { alert('Error al quitar la firma: ' + error.message); return; }
    await borrarEvidencias([f.firma_path]);
    auditEvidencia('firma_quitada', { etapa: f.etapa, rol: f.rol, nombre: f.nombre });
    await loadEvidence(activePermit.id);
  }

  async function handleFoto(foto: Blob, origen: OrigenFoto) {
    if (!activePermit) return;
    setSavingFoto(true);
    const path = rutaEvidencia(companyId, activePermit.id, 'fotos');
    const upErr = await subirEvidencia(path, foto);
    if (upErr) { setSavingFoto(false); alert('Error al subir la foto: ' + upErr); return; }
    const { data, error } = await supabase
      .from('work_permit_photos')
      .insert({ company_id: companyId, permit_id: activePermit.id, foto_path: path, origen, created_by: profile?.id ?? null })
      .select('id, tomada_en')
      .single();
    setSavingFoto(false);
    if (error) {
      await borrarEvidencias([path]);
      alert('Error al guardar la foto: ' + error.message);
      return;
    }
    auditEvidencia('foto_agregada', { origen, foto_id: (data as { id: string }).id, tomada_en: (data as { tomada_en: string }).tomada_en });
    await loadEvidence(activePermit.id);
  }

  async function handleDeleteFoto(f: Foto) {
    if (!activePermit || isLockedChecklist) return;
    const { error } = await supabase.from('work_permit_photos').delete().eq('id', f.id);
    if (error) { alert('Error al quitar la foto: ' + error.message); return; }
    await borrarEvidencias([f.foto_path]);
    auditEvidencia('foto_quitada', { foto_id: f.id });
    await loadEvidence(activePermit.id);
  }

  async function handleActivate() {
    if (!activePermit) return;
    if (faltanActivar.length > 0) { alert('Para activar el permiso falta:\n• ' + faltanActivar.join('\n• ')); return; }
    setActivating(true);
    const ahora = new Date().toISOString();
    const { error } = await supabase
      .from('work_permits')
      .update({ status: 'activo', activated_at: ahora, updated_at: ahora })
      .eq('id', activePermit.id);
    setActivating(false);
    if (error) { alert('Error al activar el permiso: ' + error.message); return; }
    auditEvidencia('permiso_activado', { firmas: firmasVerificacion.length, fotos: fotos.length });
    setActivePermit(prev => prev ? { ...prev, status: 'activo', activated_at: ahora } : prev);
    loadData();
  }

  // ── Close / cancel ───────────────────────────────────────────────────────────

  function openCloseModal() {
    if (!activePermit) return;
    setClosureNotes('');
    setCloseNombre(activePermit.authorized_by);
    setCloseCedula('');
    setCloseSigVacia(true);
    setShowCloseModal(true);
  }

  async function handleClosePermit() {
    if (!activePermit) return;
    setClosing(true);
    // Si un intento anterior ya guardó la firma de cierre, no se pide otra.
    if (faltantesParaCerrar(firmas).length > 0) {
      if (!closeNombre.trim()) { alert('El nombre de quien autoriza es requerido.'); setClosing(false); return; }
      const ok = await guardarFirma(closePadRef.current, 'cierre', 'autoriza', closeNombre, closeCedula);
      if (!ok) { setClosing(false); return; }
    }
    const ahora = new Date().toISOString();
    const { error } = await supabase
      .from('work_permits')
      .update({
        status:        'cerrado',
        closure_notes: closureNotes.trim() || null,
        closed_at:     ahora,
        updated_at:    ahora,
      })
      .eq('id', activePermit.id);
    setClosing(false);
    await loadEvidence(activePermit.id);
    if (error) { alert('Error al cerrar permiso: ' + error.message); return; }
    auditEvidencia('permiso_cerrado', { notas: closureNotes.trim() || null });
    setActivePermit(prev => prev ? { ...prev, status: 'cerrado', closed_at: ahora, closure_notes: closureNotes.trim() || null } : prev);
    setShowCloseModal(false);
    setClosureNotes('');
    loadData();
  }

  async function handleCancelPermit() {
    if (!activePermit) return;
    setCancelling(true);
    const notes = cancelReason.trim() ? `Cancelado: ${cancelReason.trim()}` : 'Cancelado';
    const { error } = await supabase
      .from('work_permits')
      .update({
        status:        'cancelado',
        closure_notes: notes,
        closed_at:     new Date().toISOString(),
        updated_at:    new Date().toISOString(),
      })
      .eq('id', activePermit.id);
    setCancelling(false);
    if (error) { alert('Error al cancelar permiso: ' + error.message); return; }
    auditEvidencia('permiso_cancelado', { motivo: cancelReason.trim() || null });
    setActivePermit(prev => prev ? { ...prev, status: 'cancelado' } : prev);
    setShowCancelModal(false);
    setCancelReason('');
    loadData();
  }

  // ── PDF ──────────────────────────────────────────────────────────────────────

  async function handlePdf() {
    if (!activePermit) return;
    setGeneratingPdf(true);
    try {
      const [firmasImg, fotosImg] = await Promise.all([
        Promise.all(firmas.map(f => evidenciaComoDataUrl(f.firma_path))),
        Promise.all(fotos.map(f => evidenciaComoDataUrl(f.foto_path))),
      ]);
      const w = activePermit.worker_id ? workers.find(x => x.id === activePermit.worker_id) : null;
      const doc = generarPermisoPDF({
        empresa:       displayCompany?.name ?? '',
        tipo:          PERMIT_TYPE_LABELS[activePermit.permit_type],
        estado:        STATUS_LABELS[activePermit.status],
        descripcion:   activePermit.work_description,
        ubicacion:     activePermit.location,
        vigenteDesde:  fechaHoraEC(activePermit.valid_from),
        vigenteHasta:  fechaHoraEC(activePermit.valid_until),
        autorizadoPor: activePermit.authorized_by,
        ejecutadoPor:  activePermit.executed_by,
        trabajador:    w ? `${w.full_name}${w.cedula ? ` (${w.cedula})` : ''}` : null,
        vigia:         activePermit.watcher_name,
        activadoEn:    activePermit.activated_at ? fechaHoraEC(activePermit.activated_at) : null,
        cerradoEn:     activePermit.status === 'cerrado' ? fechaHoraEC(activePermit.closed_at) : null,
        notasCierre:   activePermit.status === 'cerrado' ? activePermit.closure_notes : null,
        preguntas: [
          ...checklistItems.map(i => ({
            texto: i.item_text, referencia: i.referencia ?? null, propia: false,
            resultado: responseState[i.id]?.resultado ?? null, observacion: responseState[i.id]?.observation || null,
          })),
          ...customItems.map(c => ({ texto: c.item_text, propia: true, resultado: c.resultado, observacion: c.observation })),
        ],
        firmas: firmas.map((f, i) => ({ etapa: f.etapa, rol: f.rol, nombre: f.nombre, cedula: f.cedula, firmadoEn: f.firmado_en, imagen: firmasImg[i] })),
        fotos:  fotos.map((f, i) => ({ tomadaEn: f.tomada_en, imagen: fotosImg[i] })),
      });
      doc.save(nombreArchivoPermiso(PERMIT_TYPE_LABELS[activePermit.permit_type], activePermit.valid_from));
    } catch (e) {
      alert('Error al generar el PDF: ' + (e as Error).message);
    } finally {
      setGeneratingPdf(false);
    }
  }

  // ── Delete ───────────────────────────────────────────────────────────────────

  async function handleDelete() {
    if (!deleteTarget) return;
    setDeleting(true);
    const [{ data: fs }, { data: ph }] = await Promise.all([
      supabase.from('work_permit_signatures').select('firma_path').eq('permit_id', deleteTarget.id),
      supabase.from('work_permit_photos').select('foto_path').eq('permit_id', deleteTarget.id),
    ]);
    const paths = [
      ...((fs ?? []) as { firma_path: string }[]).map(f => f.firma_path),
      ...((ph ?? []) as { foto_path: string }[]).map(f => f.foto_path),
    ];
    const { error: respErr } = await supabase
      .from('work_permit_responses')
      .delete()
      .eq('permit_id', deleteTarget.id);
    if (respErr) { alert('Error al eliminar respuestas: ' + respErr.message); setDeleting(false); return; }
    // Preguntas propias, firmas y fotos se borran en cascada con el permiso.
    const { error } = await supabase.from('work_permits').delete().eq('id', deleteTarget.id);
    if (error) { alert('Error al eliminar permiso: ' + error.message); setDeleting(false); return; }
    await borrarEvidencias(paths);
    logAudit({
      company_id: companyId, action: 'deleted', entity_type: 'work_permit', entity_id: deleteTarget.id,
      entity_name: `${PERMIT_TYPE_LABELS[deleteTarget.permit_type]} — ${deleteTarget.work_description}`,
      details: { status: deleteTarget.status, archivos_borrados: paths.length },
    });
    setDeleting(false);
    setDeleteTarget(null);
    setDeleteStep(1);
    loadData();
  }

  // ── Checklist computed ───────────────────────────────────────────────────────

  const checklist = estadoChecklist(
    checklistItems.map(i => ({ resultado: responseState[i.id]?.resultado ?? null })),
    customItems,
  );
  const firmasVerificacion = firmas.filter(f => f.etapa === 'verificacion');
  const firmasCierre       = firmas.filter(f => f.etapa === 'cierre');
  const faltanActivar      = faltantesParaActivar(checklist, firmas);
  const progress       = porcentajeRespondido(checklist);
  const permitExpired  = activePermit ? isExpired(activePermit) : false;
  const isPending      = activePermit?.status === 'pendiente';
  const isActiveStatus = activePermit?.status === 'activo';
  const canActivate    = canEdit && isPending && faltanActivar.length === 0;
  const canClose       = canEdit && isActiveStatus;
  const canCancel      = canEdit && (isPending || isActiveStatus);
  // El checklist, las preguntas propias, las firmas de verificación y las fotos
  // solo se cambian mientras el permiso está pendiente.
  const isLockedChecklist = !canEdit || !isPending;
  const showFirmas     = (isPending && checklist.listoParaFirmar) || firmasVerificacion.length > 0 || fotos.length > 0;

  // ── Plan gate (after all hooks) ──────────────────────────────────────────────

  const isSuperAdmin = profile?.role === 'superadmin';

  if (!isSuperAdmin && displayCompany && !hasPlanAccess(displayCompany.plan, 'permisos-trabajo')) {
    return <UpgradeWall {...PLAN_FEATURES['permisos-trabajo']} />;
  }

  // ── Checklist view ───────────────────────────────────────────────────────────

  if (view === 'checklist' && activePermit) {
    const tc = PERMIT_TYPE_COLORS[activePermit.permit_type];
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
          <button
            onClick={() => { setView('list'); setActivePermit(null); }}
            style={{ ...ghostBtn, padding: '7px 14px' }}
          >
            ← Volver
          </button>
          <div style={{ flex: 1 }}>
            <h2 style={{ color: '#f1f5f9', fontSize: '1.15rem', fontWeight: 700, margin: 0 }}>
              {activePermit.work_description}
            </h2>
            <p style={{ color: '#64748b', fontSize: '0.8rem', margin: '2px 0 0' }}>
              {activePermit.location}
            </p>
          </div>
          <span style={{ background: tc.bg, color: tc.color, border: `1px solid ${tc.border}`, borderRadius: 6, padding: '4px 12px', fontSize: '0.8rem', fontWeight: 700 }}>
            {PERMIT_TYPE_LABELS[activePermit.permit_type]}
          </span>
          <span style={{ ...statusBadgeStyle(activePermit.status, permitExpired) }}>
            {activePermit.status === 'activo' && permitExpired ? 'Vencido' : STATUS_LABELS[activePermit.status]}
          </span>
          <button onClick={handlePdf} disabled={generatingPdf || checklistLoading} style={{ ...ghostBtn, padding: '7px 14px' }}>
            {generatingPdf ? 'Generando…' : 'Descargar PDF'}
          </button>
        </div>

        {/* Expired banner */}
        {permitExpired && (isActiveStatus || isPending) && (
          <div style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 10, padding: '12px 16px', color: '#fca5a5', fontSize: '0.86rem', fontWeight: 600 }}>
            ⚠ Este permiso ha vencido. La fecha de vigencia ({new Date(activePermit.valid_until).toLocaleString('es-EC')}) ya pasó.
          </div>
        )}

        {isPending && (
          <div style={{ background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.3)', borderRadius: 10, padding: '12px 16px', color: '#fbbf24', fontSize: '0.84rem' }}>
            Este permiso está <strong>pendiente</strong>. Responde todas las preguntas; cuando ninguna quede en "No cumple" aparecerá la sección de firmas. Se activa con la firma de quien autoriza y de cada trabajador ejecutor.
          </div>
        )}

        {/* Permit details */}
        <div style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 10, padding: '14px 20px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
          <DetailField label="Vigencia desde" value={new Date(activePermit.valid_from).toLocaleString('es-EC')} />
          <DetailField label="Vigencia hasta"  value={new Date(activePermit.valid_until).toLocaleString('es-EC')} />
          <DetailField label="Autorizado por"  value={activePermit.authorized_by} />
          <DetailField label="Ejecutado por"   value={activePermit.executed_by} />
          {activePermit.worker_id && (() => {
            const w = workers.find(x => x.id === activePermit.worker_id);
            return w ? <DetailField label="Trabajador registrado" value={`${w.full_name}${w.cedula ? ` (${w.cedula})` : ''}`} /> : null;
          })()}
          {activePermit.watcher_name && <DetailField label="Vigía" value={activePermit.watcher_name} />}
          {activePermit.activated_at && <DetailField label="Activado" value={new Date(activePermit.activated_at).toLocaleString('es-EC')} />}
        </div>

        {/* Progress bar */}
        <div style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 10, padding: '14px 20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8, gap: 10, flexWrap: 'wrap' }}>
            <span style={{ color: '#94a3b8', fontSize: '0.8rem' }}>Progreso del checklist</span>
            <span style={{ color: '#e2e8f0', fontSize: '0.8rem', fontWeight: 700 }}>
              {checklist.respondidas}/{checklist.total} respondidas
              {checklist.noCumple > 0 && <span style={{ color: '#ef4444' }}> · {checklist.noCumple} No cumple</span>}
            </span>
          </div>
          <div style={{ background: '#0f172a', borderRadius: 4, height: 8, overflow: 'hidden' }}>
            <div style={{ width: `${progress}%`, height: '100%', background: checklist.listoParaFirmar ? '#2FBF71' : checklist.noCumple > 0 ? '#ef4444' : '#3b82f6', borderRadius: 4, transition: 'width 0.3s' }} />
          </div>
        </div>

        {checklistLoading ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#475569', fontSize: '0.88rem' }}>Cargando checklist…</div>
        ) : (
          <div style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 12, overflow: 'hidden' }}>
            {checklistItems.length === 0 && (
              <div style={{ padding: 24, color: '#fbbf24', fontSize: '0.84rem' }}>
                Este tipo de permiso todavía no tiene preguntas en el catálogo.
              </div>
            )}
            {checklistItems.map((item, idx) => {
              const rs = responseState[item.id] ?? { resultado: null, observation: '' };
              return (
                <QuestionRow
                  key={item.id}
                  numero={idx + 1}
                  texto={item.item_text}
                  referencia={item.referencia ?? null}
                  resultado={rs.resultado}
                  observation={rs.observation}
                  saving={savingItem[item.id] ?? false}
                  locked={isLockedChecklist}
                  onResultado={r => handleResultado(item.id, r)}
                  onObservationChange={v => setResponseState(s => ({ ...s, [item.id]: { ...s[item.id], observation: v } }))}
                  onObservationBlur={v => handleObservationBlur(item.id, v)}
                />
              );
            })}
            {customItems.map((item, idx) => (
              <QuestionRow
                key={item.id}
                numero={checklistItems.length + idx + 1}
                texto={item.item_text}
                propia
                resultado={item.resultado}
                observation={item.observation ?? ''}
                saving={savingItem[item.id] ?? false}
                locked={isLockedChecklist}
                onResultado={r => handleCustomResultado(item, r)}
                onObservationChange={v => setCustomItems(cs => cs.map(c => c.id === item.id ? { ...c, observation: v } : c))}
                onObservationBlur={v => handleCustomObservationBlur(item, v)}
                onDelete={isLockedChecklist ? undefined : () => handleDeleteQuestion(item)}
              />
            ))}

            {!isLockedChecklist && (
              <div style={{ padding: '14px 20px', borderTop: '1px solid rgba(51,65,85,0.5)' }}>
                {!showAddQuestion ? (
                  <button onClick={() => setShowAddQuestion(true)} style={ghostBtn}>+ Agregar pregunta</button>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    <textarea
                      style={{ ...inp, minHeight: 60, resize: 'vertical' }}
                      value={newQuestion}
                      maxLength={500}
                      onChange={e => setNewQuestion(e.target.value)}
                      placeholder="Escribe la pregunta (se responde Cumple / No cumple / No aplica)…"
                    />
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#cbd5e1', fontSize: '0.8rem', cursor: 'pointer' }}>
                      <input type="checkbox" checked={saveAsTemplate} onChange={e => setSaveAsTemplate(e.target.checked)} />
                      Guardar para los próximos permisos de este tipo
                    </label>
                    <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                      <button onClick={() => { setShowAddQuestion(false); setNewQuestion(''); setSaveAsTemplate(false); }} style={ghostBtn}>Cancelar</button>
                      <button onClick={handleAddQuestion} disabled={addingQuestion} style={{ ...ghostBtn, borderColor: '#2FBF71', color: '#2FBF71' }}>
                        {addingQuestion ? 'Agregando…' : 'Agregar'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Firmas y fotos de la verificación */}
        {!checklistLoading && showFirmas && (
          <div style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 12, padding: esMovil ? '14px 10px' : '16px 20px', display: 'flex', flexDirection: 'column', gap: 16 }}>
            <h3 style={{ color: '#f1f5f9', fontSize: '1rem', fontWeight: 700, margin: 0 }}>Firmas</h3>

            {firmasVerificacion.length > 0 ? (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 12 }}>
                {firmasVerificacion.map(f => (
                  <SignatureCard key={f.id} firma={f} url={evidenceUrls[f.firma_path]} onDelete={isLockedChecklist ? undefined : () => handleDeleteSignature(f)} />
                ))}
              </div>
            ) : (
              <p style={{ color: '#64748b', fontSize: '0.82rem', margin: 0 }}>Aún no hay firmas.</p>
            )}

            {!isLockedChecklist && checklist.listoParaFirmar && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, background: '#0f172a', border: '1px solid #334155', borderRadius: 10, padding: esMovil ? 8 : 14 }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 10 }}>
                  <Field label="Rol en el permiso *">
                    <select style={sel} value={sigRol} onChange={e => setSigRol(e.target.value as RolFirma)}>
                      <option value="autoriza">{ROL_FIRMA_LABELS.autoriza}</option>
                      <option value="ejecutor">{ROL_FIRMA_LABELS.ejecutor}</option>
                    </select>
                  </Field>
                  <Field label="Nombre *">
                    <input style={inp} value={sigNombre} onChange={e => setSigNombre(e.target.value)} placeholder="Nombre de quien firma" />
                  </Field>
                  <Field label="Cédula (opcional)">
                    <input style={inp} value={sigCedula} maxLength={20} onChange={e => setSigCedula(e.target.value)} />
                  </Field>
                </div>
                <SignaturePad ref={sigPadRef} onChange={setSigVacia} disabled={savingSig} />
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  <span style={{ color: '#64748b', fontSize: '0.74rem' }}>La fecha y hora se registran al guardar.</span>
                  <button type="button" className="ss-tap" onClick={handleAddSignature} disabled={savingSig || sigVacia} style={{ ...ghostBtn, borderColor: '#2FBF71', color: '#2FBF71', opacity: sigVacia ? 0.5 : 1, padding: '8px 16px', fontSize: '0.84rem' }}>
                    {savingSig ? 'Guardando…' : 'Guardar firma'}
                  </button>
                </div>
              </div>
            )}

            <h3 style={{ color: '#f1f5f9', fontSize: '1rem', fontWeight: 700, margin: '4px 0 0' }}>Fotos</h3>
            {fotos.length > 0 ? (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 10 }}>
                {fotos.map(f => (
                  <div key={f.id} style={{ background: '#0f172a', border: '1px solid #334155', borderRadius: 8, overflow: 'hidden' }}>
                    {evidenceUrls[f.foto_path]
                      ? <img src={evidenceUrls[f.foto_path]} alt="Foto de la verificación" style={{ width: '100%', height: 110, objectFit: 'cover', display: 'block' }} />
                      : <div style={{ height: 110 }} />}
                    <div style={{ padding: '6px 8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 6 }}>
                      <span style={{ color: '#64748b', fontSize: '0.7rem' }}>{fechaHoraEC(f.tomada_en)}</span>
                      {!isLockedChecklist && (
                        <button onClick={() => handleDeleteFoto(f)} style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', fontSize: '0.72rem' }}>Quitar</button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p style={{ color: '#64748b', fontSize: '0.82rem', margin: 0 }}>Aún no hay fotos.</p>
            )}
            {!isLockedChecklist && <PhotoCapture onFoto={handleFoto} disabled={savingFoto} />}
          </div>
        )}

        {/* Firma de cierre (registrada) */}
        {firmasCierre.length > 0 && (
          <div style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 12, padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 12 }}>
            <h3 style={{ color: '#f1f5f9', fontSize: '1rem', fontWeight: 700, margin: 0 }}>Cierre</h3>
            {activePermit.closure_notes && <p style={{ color: '#94a3b8', fontSize: '0.84rem', margin: 0 }}>{activePermit.closure_notes}</p>}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 12 }}>
              {firmasCierre.map(f => <SignatureCard key={f.id} firma={f} url={evidenceUrls[f.firma_path]} />)}
            </div>
          </div>
        )}

        {canEdit && (isPending || isActiveStatus) && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 10, paddingTop: 4, flexWrap: 'wrap' }}>
            {isPending && faltanActivar.length > 0 && (
              <span style={{ color: '#64748b', fontSize: '0.78rem', flex: '1 1 240px' }}>Falta: {faltanActivar.join(' · ')}</span>
            )}
            {canCancel && (
              <button type="button" className="ss-tap" onClick={() => setShowCancelModal(true)} style={{ ...cancelBtn, borderColor: '#ef4444', color: '#ef4444' }}>
                Cancelar permiso
              </button>
            )}
            {isPending && (
              <button
                type="button"
                className="ss-tap"
                onClick={handleActivate}
                disabled={!canActivate || activating}
                style={{ ...primaryBtn, opacity: !canActivate ? 0.5 : 1, cursor: !canActivate ? 'not-allowed' : 'pointer' }}
              >
                {activating ? 'Activando…' : 'Activar permiso'}
              </button>
            )}
            {isActiveStatus && (
              <button type="button" className="ss-tap" onClick={openCloseModal} disabled={!canClose} style={primaryBtn}>
                Cerrar permiso
              </button>
            )}
          </div>
        )}

        {/* ── Close modal ──────────────────────────────────────────────────────── */}
        {showCloseModal && (
          <div style={overlay} onClick={e => e.target === e.currentTarget && !closing && setShowCloseModal(false)}>
            <div style={{ ...modalBox, maxWidth: 520 }}>
              <div style={modalHeader}>
                <h2 style={{ color: '#f1f5f9', fontSize: '1.05rem', fontWeight: 700, margin: 0 }}>Cerrar permiso</h2>
                <button onClick={() => !closing && setShowCloseModal(false)} style={closeBtn}>✕</button>
              </div>
              <div style={{ padding: esMovil ? '0 12px 16px' : '0 24px 24px', marginTop: 16, display: 'flex', flexDirection: 'column', gap: 14 }}>
                <Field label="Notas de cierre (opcional)">
                  <textarea
                    style={{ ...inp, minHeight: 80, resize: 'vertical' }}
                    value={closureNotes}
                    onChange={e => setClosureNotes(e.target.value)}
                    placeholder="Observaciones finales del trabajo realizado…"
                  />
                </Field>
                {firmasCierre.length > 0 ? (
                  <p style={{ color: '#94a3b8', fontSize: '0.82rem', margin: 0 }}>La firma de cierre ya está registrada ({firmasCierre[0].nombre}).</p>
                ) : (
                  <>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10 }}>
                      <Field label="Quien autoriza *">
                        <input style={inp} value={closeNombre} onChange={e => setCloseNombre(e.target.value)} />
                      </Field>
                      <Field label="Cédula (opcional)">
                        <input style={inp} value={closeCedula} maxLength={20} onChange={e => setCloseCedula(e.target.value)} />
                      </Field>
                    </div>
                    <Field label="Firma de cierre de quien autoriza *">
                      <SignaturePad ref={closePadRef} onChange={setCloseSigVacia} disabled={closing} />
                    </Field>
                  </>
                )}
              </div>
              <div style={modalFooter}>
                <button onClick={() => !closing && setShowCloseModal(false)} style={cancelBtn}>Cancelar</button>
                <button
                  type="button"
                  className="ss-tap"
                  onClick={handleClosePermit}
                  disabled={closing || (firmasCierre.length === 0 && closeSigVacia)}
                  style={{ ...saveBtn, opacity: closing || (firmasCierre.length === 0 && closeSigVacia) ? 0.6 : 1 }}
                >
                  {closing ? 'Cerrando…' : 'Firmar y cerrar'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Cancel modal ─────────────────────────────────────────────────────── */}
        {showCancelModal && (
          <div style={overlay} onClick={e => e.target === e.currentTarget && !cancelling && setShowCancelModal(false)}>
            <div style={{ ...modalBox, maxWidth: 480 }}>
              <div style={modalHeader}>
                <h2 style={{ color: '#f1f5f9', fontSize: '1.05rem', fontWeight: 700, margin: 0 }}>Cancelar permiso</h2>
                <button onClick={() => !cancelling && setShowCancelModal(false)} style={closeBtn}>✕</button>
              </div>
              <div style={{ padding: '0 24px 24px', marginTop: 16 }}>
                <Field label="Motivo de cancelación">
                  <textarea
                    style={{ ...inp, minHeight: 80, resize: 'vertical' }}
                    value={cancelReason}
                    onChange={e => setCancelReason(e.target.value)}
                    placeholder="Indica por qué se cancela este permiso…"
                  />
                </Field>
              </div>
              <div style={modalFooter}>
                <button onClick={() => !cancelling && setShowCancelModal(false)} style={cancelBtn}>Atrás</button>
                <button onClick={handleCancelPermit} disabled={cancelling} style={{ ...saveBtn, background: '#dc2626', opacity: cancelling ? 0.7 : 1 }}>
                  {cancelling ? 'Cancelando…' : 'Confirmar cancelación'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ── List view ────────────────────────────────────────────────────────────────

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h2 style={{ color: '#f1f5f9', fontSize: '1.25rem', fontWeight: 700, margin: 0 }}>Permisos de Trabajo</h2>
          <p style={{ color: '#64748b', fontSize: '0.82rem', margin: '4px 0 0' }}>
            Permisos para trabajos de alto riesgo: altura, caliente, espacio confinado, eléctrico, químico, LOTO, excavación, maquinaria pesada y vías públicas
          </p>
        </div>
        {canEdit && (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button className="ss-tap" onClick={() => setVerProcedimientos(v => !v)} aria-pressed={verProcedimientos} style={{ ...cancelBtn, padding: '9px 16px' }}>
              {verProcedimientos ? 'Ocultar procedimientos' : 'Procedimientos'}
            </button>
            <button className="ss-tap" onClick={abrirPlantillas} style={{ ...cancelBtn, padding: '9px 16px' }}>
              Preguntas propias
            </button>
            <button className="ss-tap" onClick={() => { setForm(makeEmptyForm()); setHastaEditado(false); setTrainingWarning(null); setShowCreate(true); }} style={primaryBtn}>
              + Nuevo permiso
            </button>
          </div>
        )}
      </div>

      {verProcedimientos && (
        <ProcedimientosPanel companyId={companyId} empresa={displayCompany?.name ?? ''} usuarioId={profile?.id ?? null} puedeEditar={canEdit} esAdmin={canDelete} />
      )}

      {/* Metrics */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12 }}>
        <MetricCard label="Total permisos"       value={totalPermits} color="#64748b" />
        <MetricCard label="Pendientes de firmas" value={pendingCount} color="#f59e0b" />
        <MetricCard label="Activos ahora"        value={activeNow}    color="#2FBF71" />
        <MetricCard label="Vencidos sin cerrar"  value={expiredOpen}  color="#ef4444" />
        <MetricCard label="Cerrados"             value={closedCount}  color="#94a3b8" />
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        <select value={filterType} onChange={e => setFilterType(e.target.value as PermitType | '')} style={{ ...sel, maxWidth: 200 }}>
          <option value="">Todos los tipos</option>
          <option value="altura">Trabajo en altura</option>
          <option value="caliente">Trabajo en caliente</option>
          <option value="confinado">Espacio confinado</option>
          <option value="electrico">Trabajo eléctrico</option>
          <option value="quimico">Químico / Sustancias Peligrosas</option>
          <option value="loto">Bloqueo y Etiquetado (LOTO)</option>
          <option value="excavacion">Excavación / Zanjas</option>
          <option value="maquinaria">Maquinaria Pesada</option>
          <option value="vias">Trabajo en Vías Públicas</option>
        </select>
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value as PermitStatus | '')} style={{ ...sel, maxWidth: 200 }}>
          <option value="">Todos los estados</option>
          <option value="pendiente">Pendiente de firmas</option>
          <option value="activo">Activo</option>
          <option value="cerrado">Cerrado</option>
          <option value="vencido">Vencido</option>
          <option value="cancelado">Cancelado</option>
        </select>
        <input
          value={filterSearch}
          onChange={e => setFilterSearch(e.target.value)}
          placeholder="Buscar por descripción o ubicación…"
          style={{ ...inp, maxWidth: 280, flex: 1 }}
        />
        {(filterType || filterStatus || filterSearch) && (
          <button onClick={() => { setFilterType(''); setFilterStatus(''); setFilterSearch(''); }} style={ghostBtn}>
            Limpiar filtros
          </button>
        )}
      </div>

      {/* Table */}
      <div style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 14, overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#475569', fontSize: '0.88rem' }}>Cargando…</div>
        ) : filteredPermits.length === 0 ? (
          <div style={{ padding: 48, textAlign: 'center', color: '#475569', fontSize: '0.88rem' }}>
            {permits.length === 0 ? 'Aún no hay permisos de trabajo registrados.' : 'Ningún permiso coincide con los filtros activos.'}
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.84rem' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #334155' }}>
                  {['Tipo', 'Descripción', 'Ubicación', 'Vigencia', 'Estado', 'Checklist', ''].map(h => (
                    <th key={h} style={thStyle}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredPermits.map(p => {
                  const tc       = PERMIT_TYPE_COLORS[p.permit_type];
                  const expired  = isExpired(p);
                  const progress = calcProgress(p);
                  return (
                    <tr key={p.id}
                      style={{ borderBottom: '1px solid rgba(51,65,85,0.5)', transition: 'background 0.1s' }}
                      onMouseEnter={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.02)')}
                      onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>

                      <td style={td}>
                        <span style={{ background: tc.bg, color: tc.color, border: `1px solid ${tc.border}`, borderRadius: 6, padding: '2px 8px', fontSize: '0.74rem', fontWeight: 700, whiteSpace: 'nowrap' }}>
                          {PERMIT_TYPE_LABELS[p.permit_type]}
                        </span>
                      </td>
                      <td style={{ ...td, color: '#e2e8f0', maxWidth: 240 }}>{p.work_description}</td>
                      <td style={{ ...td, color: '#94a3b8' }}>{p.location}</td>
                      <td style={{ ...td, color: '#94a3b8', whiteSpace: 'nowrap', fontSize: '0.78rem' }}>
                        {new Date(p.valid_from).toLocaleDateString('es-EC')} – {new Date(p.valid_until).toLocaleDateString('es-EC')}
                      </td>
                      <td style={td}>
                        <span style={statusBadgeStyle(p.status, expired)}>
                          {p.status === 'activo' && expired ? 'Vencido' : STATUS_LABELS[p.status]}
                        </span>
                      </td>
                      <td style={td}>
                        <span style={{ color: progress === 100 ? '#2FBF71' : progress > 0 ? '#fbbf24' : '#475569', fontWeight: 700, fontSize: '0.88rem' }}>
                          {progress}%
                        </span>
                      </td>
                      <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                        <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                          <button onClick={() => openChecklist(p)} style={ghostBtn}
                            onMouseEnter={e => { e.currentTarget.style.borderColor = '#60a5fa'; e.currentTarget.style.color = '#60a5fa'; }}
                            onMouseLeave={e => { e.currentTarget.style.borderColor = '#334155'; e.currentTarget.style.color = '#94a3b8'; }}>
                            {p.status === 'activo' || p.status === 'pendiente' ? 'Continuar' : 'Ver'}
                          </button>
                          {canDelete && (
                            <button onClick={() => { setDeleteTarget(p); setDeleteStep(1); }} style={ghostBtn}
                              onMouseEnter={e => { e.currentTarget.style.borderColor = '#ef4444'; e.currentTarget.style.color = '#ef4444'; }}
                              onMouseLeave={e => { e.currentTarget.style.borderColor = '#334155'; e.currentTarget.style.color = '#94a3b8'; }}>
                              Eliminar
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {permits.length > 0 && (
        <p style={{ color: '#475569', fontSize: '0.78rem', textAlign: 'right', margin: 0 }}>
          {filteredPermits.length} de {permits.length} permiso{permits.length !== 1 ? 's' : ''}
        </p>
      )}

      {/* ── Create Modal ─────────────────────────────────────────────────────── */}
      {showCreate && (
        <div style={overlay} onClick={e => e.target === e.currentTarget && !creating && setShowCreate(false)}>
          <div style={modalBox}>
            <div style={modalHeader}>
              <h2 style={{ color: '#f1f5f9', fontSize: '1.05rem', fontWeight: 700, margin: 0 }}>Nuevo permiso de trabajo</h2>
              <button onClick={() => !creating && setShowCreate(false)} style={closeBtn}>✕</button>
            </div>
            <div style={{ padding: '0 24px 24px', display: 'flex', flexDirection: 'column', gap: 14, marginTop: 20 }}>
              <Field label="Tipo de permiso *">
                <select style={sel} value={form.permit_type} onChange={e => setForm(f => ({ ...f, permit_type: e.target.value as PermitType }))}>
                  <option value="altura">Trabajo en altura</option>
                  <option value="caliente">Trabajo en caliente</option>
                  <option value="confinado">Espacio confinado</option>
                  <option value="electrico">Trabajo eléctrico</option>
                  <option value="quimico">Químico / Sustancias Peligrosas</option>
                  <option value="loto">Bloqueo y Etiquetado (LOTO)</option>
                  <option value="excavacion">Excavación / Zanjas</option>
                  <option value="maquinaria">Maquinaria Pesada</option>
                  <option value="vias">Trabajo en Vías Públicas</option>
                </select>
              </Field>
              <Field label="Descripción del trabajo *">
                <textarea
                  style={{ ...inp, minHeight: 70, resize: 'vertical' }}
                  value={form.work_description}
                  onChange={e => setForm(f => ({ ...f, work_description: e.target.value }))}
                  placeholder="Describe la actividad a realizar…"
                />
              </Field>
              <Field label="Ubicación *">
                <input style={inp} value={form.location} onChange={e => setForm(f => ({ ...f, location: e.target.value }))} placeholder="Ej: Bodega 2, techo nave A" />
              </Field>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
                <Field label="Vigente desde *">
                  <input
                    style={inp}
                    type="datetime-local"
                    value={form.valid_from}
                    onChange={e => {
                      const desde = e.target.value;
                      // Mientras no se edite a mano, "hasta" sigue a "desde" + 24 h.
                      setForm(f => ({
                        ...f,
                        valid_from:  desde,
                        valid_until: !hastaEditado && desde ? toLocalDateTimeInput(vigenteHastaPorDefecto(new Date(desde))) : f.valid_until,
                      }));
                    }}
                  />
                </Field>
                <Field label="Vigente hasta * (por defecto 24 h)">
                  <input style={inp} type="datetime-local" value={form.valid_until} onChange={e => { setHastaEditado(true); setForm(f => ({ ...f, valid_until: e.target.value })); }} />
                </Field>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
                <Field label="Trabajador registrado (opcional)">
                  <select
                    style={sel}
                    value={form.worker_id}
                    onChange={e => {
                      const w = workers.find(x => x.id === e.target.value);
                      setForm(f => ({
                        ...f,
                        worker_id:   e.target.value,
                        executed_by: w ? w.full_name : f.executed_by,
                      }));
                    }}
                  >
                    <option value="">— Sin trabajador registrado —</option>
                    {workers.map(w => (
                      <option key={w.id} value={w.id}>
                        {w.full_name}{w.cedula ? ` (${w.cedula})` : ''}
                      </option>
                    ))}
                  </select>
                  {trainingWarning && (
                    <div style={{
                      display: 'flex', alignItems: 'flex-start', gap: 8, marginTop: 8,
                      padding: '10px 12px', borderRadius: 8,
                      background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.35)',
                    }}>
                      <span style={{ flexShrink: 0, fontSize: '0.9rem' }}>⚠️</span>
                      <span style={{ color: '#fbbf24', fontSize: '0.78rem', lineHeight: 1.4 }}>
                        Este trabajador no tiene capacitación vigente en <strong>{trainingWarning.toLowerCase()}</strong>. Verifica antes de autorizar.
                      </span>
                    </div>
                  )}
                </Field>
                <Field label="Autorizado por *">
                  <input style={inp} value={form.authorized_by} onChange={e => setForm(f => ({ ...f, authorized_by: e.target.value }))} placeholder="Nombre del autorizante" />
                </Field>
              </div>
              {suggestedPermitTypes.length > 0 && (
                <div style={suggestedBoxStyle}>
                  <p style={suggestedLabelStyle}>💡 Tipos de permiso sugeridos según el puesto</p>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {suggestedPermitTypes.map(pt => {
                      const isActive = form.permit_type === pt;
                      return (
                        <button
                          key={pt}
                          type="button"
                          style={{ ...suggestedChipStyle, ...(isActive ? suggestedChipActiveStyle : {}) }}
                          onClick={() => setForm(f => ({ ...f, permit_type: pt }))}
                        >
                          {PERMIT_TYPE_LABELS[pt]}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
              <Field label="Ejecutado por *">
                <input style={inp} value={form.executed_by} onChange={e => setForm(f => ({ ...f, executed_by: e.target.value }))} placeholder="Nombre del ejecutor o contratista" />
              </Field>
              <Field label="Vigía (opcional)">
                <input style={inp} value={form.watcher_name} onChange={e => setForm(f => ({ ...f, watcher_name: e.target.value }))} placeholder="Relevante para trabajo en caliente y espacio confinado" />
              </Field>
            </div>
            <div style={modalFooter}>
              <button onClick={() => !creating && setShowCreate(false)} style={cancelBtn}>Cancelar</button>
              <button onClick={handleCreate} disabled={creating} style={{ ...saveBtn, opacity: creating ? 0.7 : 1 }}>
                {creating ? 'Creando…' : 'Crear e iniciar checklist'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Preguntas propias guardadas ──────────────────────────────────────── */}
      {showPlantillas && (
        <div style={overlay} onClick={e => e.target === e.currentTarget && cerrarPlantillas()}>
          <div style={{ ...modalBox, maxWidth: 680 }} role="dialog" aria-modal="true" aria-labelledby="plantillas-titulo">
            <div style={modalHeader}>
              <h2 id="plantillas-titulo" style={{ color: '#f1f5f9', fontSize: '1.05rem', fontWeight: 700, margin: 0 }}>Preguntas propias guardadas</h2>
              <button className="ss-tap" onClick={cerrarPlantillas} style={closeBtn} aria-label="Cerrar">✕</button>
            </div>
            <div style={{ padding: esMovil ? '16px' : '16px 24px 24px', display: 'flex', flexDirection: 'column', gap: 12 }}>
              <p style={{ color: '#94a3b8', fontSize: '0.8rem', margin: 0, lineHeight: 1.5 }}>
                Se copian a cada permiso nuevo del mismo tipo. Los permisos ya creados conservan sus preguntas aunque aquí se editen, desactiven o borren.
                Para agregar una, usa "+ Agregar pregunta" dentro de un permiso y marca "Guardar para los próximos permisos de este tipo".
              </p>
              {m12 === false && (
                <div style={{ background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.3)', borderRadius: 8, padding: '8px 12px', color: '#fbbf24', fontSize: '0.78rem' }}>
                  Desactivar y activar estará disponible cuando se actualice la base de datos. Mientras tanto se puede editar y borrar.
                </div>
              )}
              <select value={filtroPlantilla} onChange={e => setFiltroPlantilla(e.target.value as PermitType | '')} style={{ ...sel, maxWidth: 320 }} aria-label="Tipo de permiso">
                <option value="">Todos los tipos</option>
                {(Object.keys(PERMIT_TYPE_LABELS) as PermitType[]).map(t => (
                  <option key={t} value={t}>{PERMIT_TYPE_LABELS[t]}</option>
                ))}
              </select>
              {plantillaMsg && (
                <div role={plantillaMsg.tipo === 'error' ? 'alert' : 'status'} style={{
                  borderRadius: 8, padding: '8px 12px', fontSize: '0.8rem',
                  ...(plantillaMsg.tipo === 'error'
                    ? { background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.3)', color: '#fca5a5' }
                    : { background: 'rgba(47,191,113,0.08)', border: '1px solid rgba(47,191,113,0.3)', color: '#86efac' }),
                }}>
                  {plantillaMsg.texto}
                </div>
              )}
              {plantillasCargando ? (
                <div style={{ padding: 24, textAlign: 'center', color: '#475569', fontSize: '0.86rem' }}>Cargando…</div>
              ) : (() => {
                const visibles = plantillas.filter(p => !filtroPlantilla || p.permit_type === filtroPlantilla);
                if (visibles.length === 0) {
                  return <div style={{ padding: 24, textAlign: 'center', color: '#475569', fontSize: '0.86rem' }}>
                    {plantillas.length === 0 ? 'Todavía no hay preguntas propias guardadas.' : 'No hay preguntas guardadas para este tipo.'}
                  </div>;
                }
                const tipos = (Object.keys(PERMIT_TYPE_LABELS) as PermitType[]).filter(t => visibles.some(p => p.permit_type === t));
                return tipos.map(t => (
                  <div key={t} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <h3 style={{ color: PERMIT_TYPE_COLORS[t].color, fontSize: '0.82rem', fontWeight: 700, margin: '6px 0 0' }}>{PERMIT_TYPE_LABELS[t]}</h3>
                    {visibles.filter(p => p.permit_type === t).map(p => {
                      const ocupada = plantillaOcupada === p.id;
                      const inactiva = p.active === false;
                      return (
                        <div key={p.id} style={{ background: '#0f172a', border: '1px solid #334155', borderRadius: 10, padding: 12, display: 'flex', flexDirection: 'column', gap: 10, opacity: inactiva ? 0.75 : 1 }}>
                          {editandoId === p.id ? (
                            <>
                              <textarea
                                style={{ ...inp, minHeight: 60, resize: 'vertical' }}
                                value={textoEditado}
                                maxLength={500}
                                onChange={e => setTextoEditado(e.target.value)}
                                aria-label="Texto de la pregunta"
                                autoFocus
                              />
                              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                                <button className="ss-tap" onClick={() => setEditandoId(null)} disabled={ocupada} style={ghostBtn}>Cancelar</button>
                                <button className="ss-tap" onClick={() => guardarTextoPlantilla(p)} disabled={ocupada} style={{ ...ghostBtn, borderColor: '#2FBF71', color: '#2FBF71' }}>
                                  {ocupada ? 'Guardando…' : 'Guardar'}
                                </button>
                              </div>
                            </>
                          ) : (
                            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap' }}>
                              <p style={{ color: '#e2e8f0', fontSize: '0.86rem', margin: 0, lineHeight: 1.5, flex: '1 1 220px', minWidth: 0, overflowWrap: 'anywhere' }}>{p.item_text}</p>
                              {m12 && (
                                <span style={{
                                  fontSize: '0.7rem', fontWeight: 700, borderRadius: 6, padding: '2px 8px', whiteSpace: 'nowrap',
                                  ...(inactiva
                                    ? { background: 'rgba(100,116,139,0.15)', color: '#94a3b8', border: '1px solid rgba(100,116,139,0.35)' }
                                    : { background: 'rgba(47,191,113,0.12)', color: '#2FBF71', border: '1px solid rgba(47,191,113,0.3)' }),
                                }}>
                                  {inactiva ? 'Desactivada' : 'Activa'}
                                </span>
                              )}
                            </div>
                          )}

                          {editandoId !== p.id && borrarPlantilla?.id !== p.id && (
                            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                              <button className="ss-tap" onClick={() => { setPlantillaMsg(null); setBorrarPlantilla(null); setEditandoId(p.id); setTextoEditado(p.item_text); }} disabled={ocupada} style={ghostBtn}>Editar</button>
                              {m12 && (
                                <button className="ss-tap" onClick={() => { setPlantillaMsg(null); cambiarActivaPlantilla(p); }} disabled={ocupada} style={ghostBtn}>
                                  {ocupada ? 'Guardando…' : inactiva ? 'Activar' : 'Desactivar'}
                                </button>
                              )}
                              {canDelete && (
                                <button className="ss-tap" onClick={() => { setPlantillaMsg(null); setEditandoId(null); setBorrarPlantilla({ id: p.id, paso: 1 }); }} disabled={ocupada} style={{ ...ghostBtn, borderColor: 'rgba(239,68,68,0.4)', color: '#f87171' }}>Borrar</button>
                              )}
                            </div>
                          )}

                          {borrarPlantilla?.id === p.id && (
                            <div style={{ background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 8, padding: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
                              {borrarPlantilla.paso === 1 ? (
                                <>
                                  <p style={{ color: '#fca5a5', fontSize: '0.8rem', margin: 0 }}>
                                    ¿Borrar esta pregunta guardada? Ya no se copiará en los permisos nuevos; los permisos ya creados la conservan.
                                  </p>
                                  <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                                    <button className="ss-tap" onClick={() => setBorrarPlantilla(null)} style={ghostBtn}>Cancelar</button>
                                    <button className="ss-tap" onClick={() => setBorrarPlantilla({ id: p.id, paso: 2 })} style={{ ...ghostBtn, borderColor: '#ef4444', color: '#ef4444' }}>Continuar</button>
                                  </div>
                                </>
                              ) : (
                                <>
                                  <p style={{ color: '#fca5a5', fontSize: '0.8rem', fontWeight: 600, margin: 0 }}>
                                    ⚠ Confirma que deseas borrar definitivamente esta pregunta guardada. Esta acción no se puede deshacer.
                                  </p>
                                  <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                                    <button className="ss-tap" onClick={() => setBorrarPlantilla({ id: p.id, paso: 1 })} disabled={ocupada} style={ghostBtn}>Atrás</button>
                                    <button className="ss-tap" onClick={() => borrarPlantillaDefinitivo(p)} disabled={ocupada} style={{ ...ghostBtn, background: '#dc2626', borderColor: '#dc2626', color: '#fff' }}>
                                      {ocupada ? 'Borrando…' : 'Borrar definitivamente'}
                                    </button>
                                  </div>
                                </>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ));
              })()}
            </div>
            <div style={modalFooter}>
              <button className="ss-tap" onClick={cerrarPlantillas} style={cancelBtn}>Cerrar</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Delete Modal ─────────────────────────────────────────────────────── */}
      {deleteTarget && (
        <div style={overlay} onClick={e => e.target === e.currentTarget && !deleting && setDeleteTarget(null)}>
          <div style={{ ...modalBox, maxWidth: 420 }}>
            <div style={modalHeader}>
              <h2 style={{ color: '#f1f5f9', fontSize: '1.05rem', fontWeight: 700, margin: 0 }}>Eliminar permiso</h2>
              <button onClick={() => !deleting && setDeleteTarget(null)} style={closeBtn}>✕</button>
            </div>
            <div style={{ padding: '20px 24px' }}>
              {deleteStep === 1 ? (
                <>
                  <p style={{ color: '#cbd5e1', fontSize: '0.88rem', margin: '0 0 8px' }}>
                    ¿Eliminar el permiso{' '}
                    <strong style={{ color: '#f1f5f9' }}>{deleteTarget.work_description}</strong>?
                  </p>
                  <p style={{ color: '#64748b', fontSize: '0.82rem', margin: '0 0 20px' }}>
                    Se eliminarán también las respuestas del checklist, las preguntas propias, las firmas y las fotos de este permiso.
                  </p>
                  <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                    <button onClick={() => setDeleteTarget(null)} style={cancelBtn}>Cancelar</button>
                    <button onClick={() => setDeleteStep(2)} style={{ ...saveBtn, background: '#dc2626' }}>Continuar</button>
                  </div>
                </>
              ) : (
                <>
                  <p style={{ color: '#fca5a5', fontSize: '0.88rem', fontWeight: 600, margin: '0 0 20px' }}>
                    ⚠ Confirma que deseas eliminar definitivamente este permiso. Esta acción no se puede deshacer.
                  </p>
                  <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                    <button onClick={() => setDeleteStep(1)} style={cancelBtn}>Atrás</button>
                    <button onClick={handleDelete} disabled={deleting} style={{ ...saveBtn, background: '#dc2626', opacity: deleting ? 0.7 : 1 }}>
                      {deleting ? 'Eliminando…' : 'Eliminar definitivamente'}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Sub-components ─────────────────────────────────────────────────────────────

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
      <label style={{ color: '#94a3b8', fontSize: '0.78rem', fontWeight: 600 }}>{label}</label>
      {children}
    </div>
  );
}

function QuestionRow({
  numero, texto, referencia, propia, resultado, observation, saving, locked,
  onResultado, onObservationChange, onObservationBlur, onDelete,
}: {
  numero: number; texto: string; referencia?: string | null; propia?: boolean;
  resultado: ResultadoPregunta | null; observation: string; saving: boolean; locked: boolean;
  onResultado: (r: ResultadoPregunta) => void;
  onObservationChange: (v: string) => void;
  onObservationBlur: (v: string) => void;
  onDelete?: () => void;
}) {
  const fondo = resultado === 'no_cumple' ? 'rgba(239,68,68,0.04)' : resultado ? 'rgba(47,191,113,0.03)' : 'transparent';
  return (
    <div style={{ padding: '16px 20px', borderBottom: '1px solid rgba(51,65,85,0.5)', background: fondo, transition: 'background 0.2s' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 200 }}>
          <p style={{ color: '#e2e8f0', fontSize: '0.88rem', margin: 0, lineHeight: 1.5 }}>
            <span style={{ color: '#64748b', marginRight: 6 }}>{numero}.</span>
            {propia && <span style={{ color: '#a5b4fc', fontSize: '0.7rem', fontWeight: 700, marginRight: 6 }}>PROPIA</span>}
            {texto}
            {saving && <span style={{ color: '#475569', fontSize: '0.72rem', marginLeft: 8 }}>guardando…</span>}
          </p>
          {referencia && <p style={{ color: '#64748b', fontSize: '0.72rem', margin: '4px 0 0' }}>Ref.: {referencia}</p>}
        </div>
        <div style={{ display: 'flex', gap: 6, flexShrink: 0, flexWrap: 'wrap' }}>
          {(['cumple', 'no_cumple', 'no_aplica'] as ResultadoPregunta[]).map(r => {
            const activo = resultado === r;
            const c = RESULTADO_COLORS[r];
            return (
              <button
                key={r}
                type="button"
                className="ss-tap"
                onClick={() => onResultado(r)}
                disabled={locked || saving}
                aria-pressed={activo}
                style={{
                  padding: '6px 12px', borderRadius: 6, border: '1px solid', fontSize: '0.76rem', fontWeight: 700,
                  cursor: locked || saving ? 'default' : 'pointer', transition: 'all 0.15s',
                  ...(activo
                    ? { background: c.bg, color: c.color, borderColor: c.color }
                    : { background: 'transparent', color: '#64748b', borderColor: '#334155' }),
                }}
              >
                {RESULTADO_LABELS[r]}
              </button>
            );
          })}
          {onDelete && (
            <button onClick={onDelete} title="Quitar pregunta" style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', fontSize: '0.9rem' }}>✕</button>
          )}
        </div>
      </div>
      <input
        style={{ ...obsInp, marginTop: 10 }}
        placeholder="Observación (opcional)…"
        value={observation}
        disabled={locked}
        onChange={e => onObservationChange(e.target.value)}
        onBlur={e => onObservationBlur(e.target.value)}
      />
    </div>
  );
}

function SignatureCard({ firma, url, onDelete }: { firma: Firma; url?: string; onDelete?: () => void }) {
  return (
    <div style={{ background: '#0f172a', border: '1px solid #334155', borderRadius: 8, overflow: 'hidden' }}>
      {url
        ? <img src={url} alt={`Firma de ${firma.nombre}`} style={{ width: '100%', height: 90, objectFit: 'contain', background: '#fff', display: 'block' }} />
        : <div style={{ height: 90, background: '#fff' }} />}
      <div style={{ padding: '8px 10px', display: 'flex', flexDirection: 'column', gap: 2 }}>
        <span style={{ color: '#e2e8f0', fontSize: '0.82rem', fontWeight: 700 }}>{firma.nombre}</span>
        <span style={{ color: '#94a3b8', fontSize: '0.74rem' }}>
          {ROL_FIRMA_LABELS[firma.rol]}{firma.cedula ? ` · C.I. ${firma.cedula}` : ''}
        </span>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ color: '#64748b', fontSize: '0.72rem' }}>{fechaHoraEC(firma.firmado_en)}</span>
          {onDelete && <button onClick={onDelete} style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', fontSize: '0.72rem' }}>Quitar</button>}
        </div>
      </div>
    </div>
  );
}

function DetailField({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <span style={{ color: '#64748b', fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.03em', textTransform: 'uppercase' }}>{label}</span>
      <span style={{ color: '#e2e8f0', fontSize: '0.86rem' }}>{value}</span>
    </div>
  );
}

function MetricCard({ label, value, color }: { label: string; value: number | string; color: string }) {
  return (
    <div style={{ background: '#1e293b', border: `1px solid ${color}30`, borderRadius: 10, padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span style={{ color, fontSize: '1.7rem', fontWeight: 800, lineHeight: 1 }}>{value}</span>
      <span style={{ color: '#64748b', fontSize: '0.75rem', lineHeight: 1.3 }}>{label}</span>
    </div>
  );
}

function statusBadgeStyle(status: PermitStatus, expired: boolean): React.CSSProperties {
  const showExpired = status === 'activo' && expired;
  const palette = showExpired
    ? { bg: 'rgba(239,68,68,0.12)',  color: '#ef4444', border: 'rgba(239,68,68,0.3)' }
    : status === 'pendiente'
    ? { bg: 'rgba(245,158,11,0.12)', color: '#fbbf24', border: 'rgba(245,158,11,0.3)' }
    : status === 'activo'
    ? { bg: 'rgba(47,191,113,0.12)', color: '#2FBF71', border: 'rgba(47,191,113,0.3)' }
    : status === 'cerrado'
    ? { bg: 'rgba(100,116,139,0.12)', color: '#94a3b8', border: 'rgba(100,116,139,0.3)' }
    : { bg: 'rgba(51,65,85,0.3)', color: '#64748b', border: 'rgba(51,65,85,0.5)' };
  return {
    background: palette.bg, color: palette.color, border: `1px solid ${palette.border}`,
    borderRadius: 6, padding: '4px 12px', fontSize: '0.78rem', fontWeight: 700, whiteSpace: 'nowrap',
  };
}

// ── Styles ─────────────────────────────────────────────────────────────────────

const primaryBtn:  React.CSSProperties = { padding: '9px 20px', background: 'linear-gradient(135deg, #1D6FA3 0%, #2FBF71 100%)', border: 'none', color: '#fff', fontWeight: 700, fontSize: '0.88rem', borderRadius: 8, cursor: 'pointer' };
const thStyle:     React.CSSProperties = { padding: '11px 14px', textAlign: 'left', color: '#64748b', fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', whiteSpace: 'nowrap' };
const td:          React.CSSProperties = { padding: '11px 14px', verticalAlign: 'middle' };
const ghostBtn:    React.CSSProperties = { padding: '5px 12px', background: 'transparent', border: '1px solid #334155', color: '#94a3b8', borderRadius: 6, cursor: 'pointer', fontSize: '0.76rem', transition: 'all 0.15s' };
const overlay:     React.CSSProperties = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(3px)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', zIndex: 400, padding: '20px 16px', overflowY: 'auto' };
const modalBox:    React.CSSProperties = { background: '#1e293b', border: '1px solid #334155', borderRadius: 16, width: '100%', maxWidth: 560, boxShadow: '0 24px 48px rgba(0,0,0,0.5)', margin: 'auto' };
const modalHeader: React.CSSProperties = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 24px', borderBottom: '1px solid #334155' };
const modalFooter: React.CSSProperties = { padding: '16px 24px', borderTop: '1px solid #334155', display: 'flex', gap: 10, justifyContent: 'flex-end', alignItems: 'center' };
const inp:         React.CSSProperties = { width: '100%', padding: '9px 12px', background: 'rgba(255,255,255,0.04)', border: '1px solid #334155', borderRadius: 8, color: '#e2e8f0', fontSize: '0.86rem', outline: 'none', boxSizing: 'border-box' };
const sel:         React.CSSProperties = { ...inp, cursor: 'pointer' };
const obsInp:      React.CSSProperties = { ...inp, padding: '7px 10px', fontSize: '0.8rem', color: '#94a3b8' };
const closeBtn:    React.CSSProperties = { background: 'none', border: 'none', color: '#64748b', fontSize: '1.2rem', cursor: 'pointer', lineHeight: 1 };
const cancelBtn:   React.CSSProperties = { padding: '9px 20px', background: 'transparent', border: '1px solid #334155', color: '#94a3b8', borderRadius: 8, cursor: 'pointer', fontSize: '0.88rem', fontWeight: 600 };
const saveBtn:     React.CSSProperties = { padding: '9px 24px', background: 'linear-gradient(135deg, #1D6FA3 0%, #2FBF71 100%)', border: 'none', color: '#fff', borderRadius: 8, cursor: 'pointer', fontSize: '0.88rem', fontWeight: 700 };
const suggestedBoxStyle:         React.CSSProperties = { background: 'rgba(99,102,241,0.06)', border: '1px solid rgba(99,102,241,0.2)', borderRadius: 10, padding: '12px 14px' };
const suggestedLabelStyle:       React.CSSProperties = { color: '#a5b4fc', fontSize: '0.76rem', fontWeight: 700, margin: '0 0 8px' };
const suggestedChipStyle:        React.CSSProperties = { padding: '5px 12px', background: 'rgba(255,255,255,0.04)', border: '1px solid #334155', color: '#cbd5e1', borderRadius: 999, cursor: 'pointer', fontSize: '0.78rem', fontWeight: 600, transition: 'all 0.15s' };
const suggestedChipActiveStyle:  React.CSSProperties = { background: 'rgba(47,191,113,0.15)', border: '1px solid #2FBF71', color: '#2FBF71' };
